const API_BASE = 'https://xceededge.com';

let currentScreen = null;
let visibleSince = null;
let accumulatedMs = 0;
let visibilityBound = false;

function getSession() {
  try {
    const raw = localStorage.getItem('xceedEdge_user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.session || null;
  } catch {
    return null;
  }
}

function resetTimer() {
  accumulatedMs = 0;
  visibleSince = typeof document !== 'undefined' && document.visibilityState === 'visible'
    ? Date.now()
    : null;
}

function activeDurationMs() {
  let total = accumulatedMs;
  if (visibleSince !== null) {
    total += Date.now() - visibleSince;
  }
  return total;
}

function sendPayload(payload, { keepalive = false } = {}) {
  const session = getSession();
  if (!session) return;

  const body = JSON.stringify({ session, ...payload });
  const url = `${API_BASE}/log-activity.php`;

  // Use fetch with credentials: 'omit' — db.php sets Allow-Credentials: true alongside
  // Allow-Origin: *, which breaks sendBeacon / credentialed cross-origin requests
  // (e.g. localhost:3000 → xceededge.com shows net::ERR_FAILED).
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'omit',
    keepalive,
  }).catch(() => {});
}

function sendEvent(event) {
  sendPayload(event, { keepalive: false });
}

export function flushCurrentScreen() {
  if (!currentScreen || !getSession()) return;

  const durationSeconds = Math.round(activeDurationMs() / 1000);
  if (durationSeconds >= 1) {
    sendPayload({
      event_type: 'page_view',
      screen_path: currentScreen.path,
      screen_label: currentScreen.label,
      course_id: currentScreen.courseId || null,
      duration_seconds: durationSeconds,
    }, { keepalive: true });
  }

  currentScreen = null;
  accumulatedMs = 0;
  visibleSince = null;
}

export function trackScreen(path, label, courseId = null) {
  if (!getSession()) return;

  if (currentScreen && currentScreen.path !== path) {
    flushCurrentScreen();
  }

  currentScreen = { path, label, courseId };
  resetTimer();
}

export function logAction(actionName, metadata = {}, overrides = {}) {
  if (!getSession()) return;

  sendEvent({
    event_type: 'action',
    screen_path: overrides.screenPath || currentScreen?.path || window.location.pathname,
    screen_label: overrides.screenLabel || currentScreen?.label || null,
    course_id: overrides.courseId ?? currentScreen?.courseId ?? null,
    action_name: actionName,
    metadata,
  });
}

export function initVisibilityTracking() {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (visibleSince !== null) {
        accumulatedMs += Date.now() - visibleSince;
        visibleSince = null;
      }
    } else if (currentScreen) {
      visibleSince = Date.now();
    }
  });

  window.addEventListener('pagehide', () => flushCurrentScreen());
}

export function getScreenLabel(pathname) {
  const labels = {
    '/': 'Home',
    '/courses': 'Courses',
    '/blog': 'Blog',
    '/about': 'About',
    '/contact': 'Contact',
    '/careers': 'Careers',
    '/auth': 'Auth',
    '/login': 'Login',
    '/forgot-password': 'Forgot Password',
    '/terms': 'Terms & Conditions',
    '/dashboard': 'Student Dashboard',
    '/assignments': 'Assignments',
    '/quizzes': 'Quizzes',
    '/certificates': 'Certificates',
    '/wishlist': 'Wishlist',
    '/profile': 'Profile',
    '/instructor': 'Instructor Dashboard',
  };

  if (labels[pathname]) return labels[pathname];
  if (pathname.startsWith('/course/')) return 'Course Detail';
  if (pathname.startsWith('/blog/')) return 'Blog Post';
  if (pathname.startsWith('/player/')) return 'Course Player';
  if (pathname.startsWith('/dashboard/')) return 'Course Dashboard';
  return pathname;
}

export function isCourseDashboardPath(pathname) {
  return /^\/dashboard\/[^/]+/.test(pathname);
}
